'use client'

import { useId } from 'react'
import { useTranslations } from 'next-intl'

import type { TagShelf } from '@/lib/blog/moods'

import { openMood } from './mood-filter'

/**
 * The blog home's editorial shelves: one card per `editorial` tag — deals,
 * practical how-tos, route guides, company news, service alerts. These are
 * content genres rather than travel vibes, so they get their own section
 * instead of competing with the mood tiles for the same shelf.
 *
 * Each card is a `<button>`, never an anchor: like every tag view it opens an
 * in-place filter with no crawlable URL (blog-moods FR-9).
 */
export const BlogEditorialSection = ({ shelves }: { shelves: TagShelf[] }) => {
    const t = useTranslations('blogHome.editorial')
    const headingId = useId()

    if (shelves.length === 0) {
        return null
    }

    return (
        <section
            aria-labelledby={headingId}
            className="border-y border-border bg-muted px-4 py-10 md:px-10 md:py-14"
            data-testid="editorial-section"
        >
            <div className="mx-auto w-full max-w-[1400px]">
                <h2
                    id={headingId}
                    className="text-2xl font-bold tracking-tight text-text-primary md:text-[28px]"
                >
                    {t('heading')}
                </h2>
                <p className="mt-1 text-[15px] text-text-secondary md:text-base">
                    {t('subheading')}
                </p>

                <ul className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-5">
                    {shelves.map((shelf) => (
                        <li key={shelf.slug}>
                            <button
                                type="button"
                                onClick={() => openMood(shelf.slug)}
                                data-testid="editorial-tile"
                                className="flex h-full w-full flex-col justify-between gap-3 rounded-xl bg-card p-5 text-left shadow-sm transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none"
                            >
                                <span className="text-lg font-bold text-text-primary">
                                    {shelf.label}
                                </span>
                                <span className="text-sm text-text-secondary">
                                    {t('count', { count: shelf.posts.length })}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}
